const API_KEY = process.env.REACT_APP_AWS_FRIENDZ_API_KEY;
const API_BASE_URL = process.env.REACT_APP_BASE_URL;

export const subscriptionTiers = {
  FREE: {
    name: 'Free',
    price: 0,
    propertyAnalyses: 5,
    chatMessages: 10,
    features: ['Advanced ROI metrics', 'Property analysis', 'PDF export', 'Property comparison']
  },
  PREMIUM: {
    name: 'Premium',
    price: 15,
    propertyAnalyses: 50,
    chatMessages: 500,
    features: ['Advanced ROI metrics', 'Property analysis', 'PDF export', 'Property comparison', 'Email support']
  },
  PRO: {
    name: 'Pro',
    price: 50,
    propertyAnalyses: 500,
    chatMessages: 1000,
    features: ['Everything in Premium', '500 property searches per month', '1000 chat messages per month']
  }
};

export const subscriptionService = {
  // Get user's current subscription from existing backend
  async getCurrentSubscription(userId) {
    try {
      // Call the existing property_roi endpoint with GET method to fetch user data
      const response = await fetch(`${API_BASE_URL}/property_roi?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        }
      });

      // Response received

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      // Processing user subscription data
      
      // Map backend subscription status to frontend tier
      const subscriptionStatusMap = {
        'free': 'FREE',
        'premium': 'PREMIUM', 
        'pro': 'PRO'
      };

      const tier = subscriptionStatusMap[userData.subscriptionStatus] || 'FREE';
      // Subscription tier mapped
      
      const subscriptionData = {
        tier: tier,
        status: userData.subscriptionStatus === 'canceled' ? 'canceled' : 'active',
        userId: userId,
        cancelAtPeriodEnd: userData.cancelAtPeriodEnd || false,
        currentPeriodEnd: userData.currentPeriodEnd || null
      };
      
      // Subscription data prepared
      return subscriptionData;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return { tier: 'FREE', status: 'active', userId: userId };
    }
  },

  // Get usage statistics from existing backend
  async getUsage(userId) {
    try {
      // Use the same GET endpoint to get user data including usage
      const response = await fetch(`${API_BASE_URL}/property_roi?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      
      // Map backend usage to frontend format
      return {
        propertyAnalyses: userData.monthlySearchCount || 0,
        chatMessages: userData.chatMessagesUsed || 0
      };
    } catch (error) {
      console.error('Error fetching usage:', error);
      return { propertyAnalyses: 0, chatMessages: 0 };
    }
  },

  // Create Stripe checkout session
  async createCheckoutSession(tier, userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          'x-user-id': userId
        },
        body: JSON.stringify({
          tier: tier,
          userId: userId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { url: data.url };
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  // Create customer portal session
  async createPortalSession(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/create-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          'x-user-id': userId
        },
        body: JSON.stringify({
          userId: userId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { url: data.url };
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  },

  // Check if user can perform an action
  canPerformAction(subscription, usage, action) {
    const tier = subscriptionTiers[subscription.tier] || subscriptionTiers.FREE;
    
    switch (action) {
      case 'property_analysis':
        return tier.propertyAnalyses === -1 || usage.propertyAnalyses < tier.propertyAnalyses;
      case 'chat_message':
        return tier.chatMessages === -1 || usage.chatMessages < tier.chatMessages;
      default:
        return false;
    }
  },

  // Get usage percentage for display
  getUsagePercentage(subscription, usage, action) {
    const tier = subscriptionTiers[subscription.tier] || subscriptionTiers.FREE;
    
    switch (action) {
      case 'property_analysis':
        if (tier.propertyAnalyses === -1) return 0; // unlimited
        return Math.min((usage.propertyAnalyses / tier.propertyAnalyses) * 100, 100);
      case 'chat_message':
        if (tier.chatMessages === -1) return 0; // unlimited
        return Math.min((usage.chatMessages / tier.chatMessages) * 100, 100);
      default:
        return 100;
    }
  }
};